<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  proposal: { type: Object, required: true },
  saving: { type: Boolean, default: false },
  stale: { type: Boolean, default: false },
  error: { type: String, default: '' },
});
const emit = defineEmits(['save', 'cancel']);
const { t } = useI18n();
const { formatMessage } = useMessageFormatter();
const draft = reactive({
  question: props.proposal.payload.question || '',
  answer: props.proposal.payload.answer || '',
  conditions: [...(props.proposal.payload.conditions || [])],
  claims: [...(props.proposal.payload.claims || [])],
});
const original = JSON.stringify(draft);
const hasVariations = Boolean(
  props.proposal.payload.question_variations?.equivalent?.length
);
const equivalentQuestions = ref(
  (props.proposal.payload.question_variations?.equivalent || [])
    .map(item => item.question)
    .join('\n')
);
const originalQuestions = equivalentQuestions.value;
const questionsChanged = computed(
  () => hasVariations && equivalentQuestions.value !== originalQuestions
);
const questions = computed(() =>
  equivalentQuestions.value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean)
);
const reason = ref('');
const groups = computed(() => [
  {
    key: 'conditions',
    label: t('CORTEX.CURATION_V6.DRAFT.CONDITION'),
    add: t('CORTEX.CURATION_V6.DRAFT.ADD_CONDITION'),
  },
  {
    key: 'claims',
    label: t('CORTEX.CURATION_V6.DRAFT.CLAIM'),
    add: t('CORTEX.CURATION_V6.DRAFT.ADD_CLAIM'),
  },
]);
const canSave = computed(
  () =>
    !props.saving &&
    !props.stale &&
    draft.question.trim() &&
    draft.answer.trim() &&
    reason.value.trim().length >= 10 &&
    reason.value.trim().length <= 500 &&
    [...draft.conditions, ...draft.claims].every(item => item.trim()) &&
    (!questionsChanged.value ||
      (questions.value.length >= 20 && questions.value.length <= 30)) &&
    (JSON.stringify(draft) !== original || questionsChanged.value)
);
const save = () => {
  if (canSave.value)
    emit('save', {
      content: {
        ...draft,
        conditions: [...draft.conditions],
        claims: [...draft.claims],
        ...(questionsChanged.value
          ? { equivalent_questions: [...questions.value] }
          : {}),
      },
      reason: reason.value.trim(),
    });
};
</script>

<template>
  <section
    class="grid min-w-0 gap-4"
    data-testid="curation-draft-editor"
    role="form"
    :aria-label="$t('CORTEX.CURATION_V6.DRAFT.EDIT')"
  >
    <p class="text-label-small leading-4 text-n-slate-11">
      {{ $t('CORTEX.CURATION_V6.DRAFT.DESCRIPTION') }}
    </p>
    <TextArea
      id="curation-draft-question"
      v-model="draft.question"
      :label="$t('CORTEX.CURATION_V6.QUESTION')"
      :max-length="96000"
      :disabled="saving"
    />
    <TextArea
      id="curation-draft-answer"
      v-model="draft.answer"
      :label="$t('CORTEX.CURATION_V6.ANSWER')"
      :message="$t('CORTEX.CURATION_V6.DRAFT.ANSWER_HINT')"
      :max-length="96000"
      :disabled="saving"
      custom-text-area-class="min-h-48"
    />
    <section
      class="grid gap-2"
      :aria-label="$t('CORTEX.CURATION_V6.DRAFT.PREVIEW')"
    >
      <p class="font-medium text-n-slate-12">
        {{ $t('CORTEX.CURATION_V6.DRAFT.PREVIEW') }}
      </p>
      <div
        v-dompurify-html="formatMessage(draft.answer, false)"
        class="prose-bubble min-w-0 break-words text-n-slate-12"
      />
    </section>
    <div v-for="group in groups" :key="group.key" class="grid gap-2">
      <div
        v-for="(_, index) in draft[group.key]"
        :key="index"
        class="flex items-start gap-2"
      >
        <TextArea
          :id="`curation-draft-${group.key}-${index}`"
          v-model="draft[group.key][index]"
          class="min-w-0 flex-1"
          :label="`${group.label} ${index + 1}`"
          :max-length="96000"
          :disabled="saving"
        />
        <NextButton
          type="button"
          sm
          outline
          slate
          icon="i-lucide-trash-2"
          :aria-label="
            $t('CORTEX.CURATION_V6.DRAFT.REMOVE', {
              item: group.label,
              index: index + 1,
            })
          "
          :disabled="saving"
          @click="draft[group.key].splice(index, 1)"
        />
      </div>
      <div class="flex justify-end">
        <NextButton
          type="button"
          sm
          outline
          icon="i-lucide-plus"
          :label="group.add"
          :disabled="saving"
          @click="draft[group.key].push('')"
        />
      </div>
    </div>
    <TextArea
      v-if="hasVariations"
      id="curation-draft-equivalent-questions"
      v-model="equivalentQuestions"
      :label="$t('CORTEX.CURATION_V6.DRAFT.EQUIVALENT_QUESTIONS')"
      :message="
        $t('CORTEX.CURATION_V6.DRAFT.QUESTIONS_HINT', {
          count: questions.length,
        })
      "
      :max-length="30030"
      :disabled="saving"
      custom-text-area-class="min-h-64"
    />
    <TextArea
      id="curation-draft-reason"
      v-model="reason"
      :label="$t('CORTEX.CURATION_V6.DRAFT.REASON')"
      :message="$t('CORTEX.CURATION_V6.DRAFT.REASON_HINT')"
      :max-length="500"
      :disabled="saving"
    />
    <p v-if="error" role="alert" class="text-n-ruby-11">{{ error }}</p>
    <div class="flex flex-wrap justify-end gap-2">
      <NextButton
        type="button"
        outline
        slate
        icon="i-lucide-x"
        :label="$t('CORTEX.CURATION_V6.DRAFT.CANCEL')"
        :disabled="saving"
        @click="emit('cancel')"
      />
      <NextButton
        type="button"
        data-testid="curation-draft-save"
        icon="i-lucide-save"
        :label="$t('CORTEX.CURATION_V6.DRAFT.SAVE')"
        :disabled="!canSave"
        :is-loading="saving"
        @click="save"
      />
    </div>
  </section>
</template>
